import { getTimeZones } from '@vvo/tzdb'

const DISPLAY_CITY_LIMIT = 4

const normalizeBaseSearchText = (text = '') => {
  if (!text) return ''

  return String(text)
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

// NFD + combining-marks strip so "São Paulo" matches "sao paulo".
const normalizeTimeZoneSearchText = (s = '') => normalizeBaseSearchText(String(s).normalize('NFD').replace(/[̀-ͯ]/g, ''))

const ADDITIONAL_SEARCH_CITIES_BY_ZONE = {
  'America/Adak': ['Atka'],
  'America/Anchorage': [
    'Sitka',
    'Ketchikan',
    'Wasilla',
    'Kodiak',
    'Nome',
    'Bethel',
    'Palmer',
    'Kenai',
    'Soldotna',
    'Homer',
  ],
  'America/Chicago': [
    'Austin',
    'Fort Worth',
    'Oklahoma City',
    'Kansas City',
    'Minneapolis',
    'St. Paul',
    'Milwaukee',
    'Nashville',
    'Memphis',
    'New Orleans',
    'St. Louis',
    'Omaha',
    'Des Moines',
    'Wichita',
    'Tulsa',
    'Baton Rouge',
    'Birmingham',
    'Little Rock',
    'Madison',
    'Green Bay',
    'Lincoln',
    'Fargo',
    'Sioux Falls',
  ],
  'America/Denver': [
    'Salt Lake City',
    'Boise',
    'Boulder',
    'Fort Collins',
    'Aurora',
    'Lakewood',
    'Provo',
    'Ogden',
    'Billings',
    'Cheyenne',
    'Missoula',
  ],
  'America/Edmonton': [
    'Banff',
    'Medicine Hat',
    'Grande Prairie',
    'Fort McMurray',
    'Yellowknife',
    'Inuvik',
    'Lloydminster',
  ],
  'America/Halifax': ['Saint John', 'Fredericton', 'Charlottetown', 'Truro', 'Summerside', 'Goose Bay'],
  'America/Los_Angeles': [
    'Seattle',
    'Portland',
    'Las Vegas',
    'Sacramento',
    'Fresno',
    'Oakland',
    'Long Beach',
    'Anaheim',
    'Riverside',
    'Santa Ana',
    'Irvine',
    'Bakersfield',
    'Stockton',
    'Reno',
    'Tacoma',
    'Spokane',
  ],
  'America/New_York': [
    'Boston',
    'Washington DC',
    'Miami',
    'Atlanta',
    'Detroit',
    'Baltimore',
    'Charlotte',
    'Orlando',
    'Tampa',
    'Jacksonville',
    'Pittsburgh',
    'Cleveland',
    'Columbus',
    'Cincinnati',
    'Indianapolis',
    'Louisville',
    'Raleigh',
    'Richmond',
    'Buffalo',
    'Providence',
    'Hartford',
    'Newark',
    'Jersey City',
    'Virginia Beach',
  ],
  'America/Phoenix': ['Scottsdale', 'Glendale', 'Tempe', 'Gilbert', 'Peoria', 'Yuma', 'Flagstaff'],
  'America/Regina': ['Yorkton', 'Swift Current', 'North Battleford'],
  'America/St_Johns': ['Gander', 'Grand Falls-Windsor', 'Conception Bay South', 'Bay Roberts'],
  'America/Toronto': [
    'Brampton',
    'Hamilton',
    'Quebec City',
    'Laval',
    'Kitchener',
    'London',
    'Windsor',
    'Oshawa',
    'Markham',
    'Vaughan',
    'Burlington',
    'Barrie',
    'Kingston',
    'Gatineau',
    'Sherbrooke',
    'Trois-Rivières',
    'Sudbury',
    'Thunder Bay',
  ],
  'America/Vancouver': [
    'Kelowna',
    'Richmond',
    'Abbotsford',
    'Coquitlam',
    'Nanaimo',
    'Kamloops',
    'Prince George',
    'North Vancouver',
  ],
  'America/Whitehorse': ['Dawson Creek', 'Fort Nelson', 'Watson Lake'],
  'America/Winnipeg': ['Portage la Prairie', 'Thompson', 'Winkler', 'Selkirk', 'Dryden', 'Fort Frances'],
  'Pacific/Honolulu': [
    'Hilo',
    'Kailua',
    'Kaneohe',
    'Kahului',
    'Kihei',
    'Mililani',
    'Ewa Beach',
    'Waipahu',
    'Lahaina',
    'Lihue',
  ],
}

const cityFromIana = (iana = '') => {
  const parts = iana.split('/')
  return parts[parts.length - 1].replace(/_/g, ' ')
}

const uniqueValues = (values = []) => [...new Set(values.filter(Boolean))]

const getTimeZoneSortTitle = (zone) => {
  const city = cityFromIana(zone?.name)
  return zone?.alternativeName ? `${city}, ${zone.alternativeName}` : city
}

const getCountrySortPriority = (zone) => (zone?.countryCode === 'US' || zone?.countryCode === 'CA' ? 0 : 1)

const getSortOffset = (zone, offsets) => {
  const offset = offsets ? offsets.get(zone) : zone?.rawOffsetInMinutes
  return Number.isFinite(offset) ? offset : Number.MAX_SAFE_INTEGER
}

// offsets is an optional zone -> offset-in-minutes map. Without it the zones sort
// by their standard offset; with it they sort by the offset in effect on the day
// of the call, which is what the list actually displays.
const compareTimeZones = (a, b, offsets) => {
  // Sort by UTC offset first, then keep North American zones nearer the top for this app's users.
  const offsetA = getSortOffset(a, offsets)
  const offsetB = getSortOffset(b, offsets)
  if (offsetA !== offsetB) return offsetA - offsetB

  const countryPriorityA = getCountrySortPriority(a)
  const countryPriorityB = getCountrySortPriority(b)
  if (countryPriorityA !== countryPriorityB) return countryPriorityA - countryPriorityB

  const titleComparison = getTimeZoneSortTitle(a).localeCompare(getTimeZoneSortTitle(b))
  if (titleComparison !== 0) return titleComparison

  return (a?.name || '').localeCompare(b?.name || '')
}

let sortedTimeZones

// Every selectable timezone, ordered west to east.
//
// Pass atTime (the call's start time) to order them by the offset they are on that
// day rather than by their standard offset. Without it, Denver and Phoenix sit
// side by side all year even though in summer they are an hour apart — and the
// line under each zone would then contradict the order they appear in.
//
// Offsets are read once per zone up front rather than inside the comparison,
// which would ask Intl for the same answer thousands of times.
export const getAllTimeZones = (atTime) => {
  if (!sortedTimeZones) sortedTimeZones = [...getTimeZones()].sort((a, b) => compareTimeZones(a, b))
  // A half-typed date can leave the form without a usable time; standard offsets
  // are a better order than none.
  if (atTime == null || !Number.isFinite(Number(atTime))) return sortedTimeZones.slice()

  const zones = sortedTimeZones.slice()
  const offsets = new Map(zones.map((zone) => [zone, getOffsetInMinutesAt(zone, atTime)]))
  return zones.sort((a, b) => compareTimeZones(a, b, offsets))
}

let timeZoneNames

const getTimeZoneNames = () => {
  if (!timeZoneNames) timeZoneNames = new Set(getAllTimeZones().map((zone) => zone.name))
  return timeZoneNames
}

let timeZoneIndex

const getTimeZoneIndex = () => {
  if (timeZoneIndex) return timeZoneIndex

  const index = {}
  for (const zone of getAllTimeZones()) {
    // Point grouped/alias IANA names back at the selectable canonical zone.
    index[zone.name] = zone
    for (const groupedName of zone.group || []) {
      if (!index[groupedName]) index[groupedName] = zone
    }
  }
  timeZoneIndex = index
  return timeZoneIndex
}

const getTimeZoneInfo = (timeZone) => {
  if (!timeZone) return null
  if (typeof timeZone === 'object') return timeZone
  return getTimeZoneIndex()[timeZone] || null
}

const getCanonicalTimeZoneName = (timeZone) => {
  if (!timeZone) return ''
  const zone = getTimeZoneInfo(timeZone)
  if (zone?.name) return zone.name
  return typeof timeZone === 'string' ? timeZone : ''
}

export const getSelectableTimeZoneName = (timeZone) => {
  if (!timeZone) return ''
  if (typeof timeZone === 'object') return timeZone.name || ''
  if (getTimeZoneNames().has(timeZone)) return timeZone
  return getCanonicalTimeZoneName(timeZone)
}

export const getTimeZoneTitle = (timeZone) => {
  if (!timeZone) return ''
  const zone = getTimeZoneInfo(timeZone)
  const name = zone?.name || getCanonicalTimeZoneName(timeZone)
  const city = cityFromIana(name)
  return zone?.alternativeName ? `${city}, ${zone.alternativeName}` : city
}

const formatOffset = (offsetInMinutes) => {
  if (!Number.isFinite(offsetInMinutes)) return ''
  const sign = offsetInMinutes >= 0 ? '+' : '-'
  const absOffset = Math.abs(offsetInMinutes)
  const hours = String(Math.floor(absOffset / 60)).padStart(2, '0')
  const minutes = String(absOffset % 60).padStart(2, '0')
  return `${sign}${hours}:${minutes}`
}

// How far the zone runs from GMT at one particular moment, in minutes.
//
// Read from Intl rather than from the timezone database's rawOffsetInMinutes,
// which is the zone's *standard* offset and ignores daylight saving: New York's
// raw offset is -300 all year, but from March to November the clocks there
// actually read GMT-04. The moment matters because a call booked in July and a
// call booked in December are genuinely in different offsets.
//
// Returns null when the browser cannot produce an offset, so callers can fall
// back to the standard offset instead of showing nothing.
const getOffsetInMinutesAt = (timeZone, atTime) => {
  const name = getCanonicalTimeZoneName(timeZone)
  const time = Number(atTime)
  // Number(null) is 0, which would silently read the offset as it was in 1970.
  if (!name || atTime == null || !Number.isFinite(time)) return null

  try {
    const formatted = new Intl.DateTimeFormat('en-US', { timeZone: name, timeZoneName: 'longOffset' }).format(
      new Date(time)
    )
    const match = formatted.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/)
    // Zones sitting exactly on GMT are printed as a bare "GMT", with no digits.
    if (!match) return /GMT/.test(formatted) ? 0 : null

    const [, sign, hours, minutes] = match
    const offset = Number(hours) * 60 + Number(minutes || 0)
    return sign === '-' ? -offset : offset
  } catch {
    return null
  }
}

const getOffsetSearchValues = (offsetInMinutes) => {
  if (!Number.isFinite(offsetInMinutes)) return []
  const sign = offsetInMinutes >= 0 ? '+' : '-'
  const absOffset = Math.abs(offsetInMinutes)
  const hours = Math.floor(absOffset / 60)
  const paddedHours = String(hours).padStart(2, '0')
  const minutes = String(absOffset % 60).padStart(2, '0')
  const fullOffset = `${sign}${paddedHours}:${minutes}`
  const shortHourOffset = `${sign}${hours}`
  const paddedHourOffset = `${sign}${paddedHours}`

  return [
    `GMT${fullOffset}`,
    `GMT${shortHourOffset}`,
    `GMT${paddedHourOffset}`,
    `UTC${fullOffset}`,
    `UTC${shortHourOffset}`,
    `UTC${paddedHourOffset}`,
  ]
}

const parseSignedOffsetQuery = (rawQuery = '') => {
  // Only exact signed GMT/UTC offset queries should bypass normal fuzzy text search.
  const match = String(rawQuery)
    .trim()
    .match(/^(?:gmt|utc)\s*([+-])\s*(\d{1,2})(?::?(\d{2}))?$/i)
  if (!match) return null

  const [, sign, rawHours, rawMinutes] = match
  const hours = Number(rawHours)
  const minutes = rawMinutes == null ? 0 : Number(rawMinutes)
  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || minutes >= 60) return null

  const offset = hours * 60 + minutes
  return sign === '-' ? -offset : offset
}

const getTimeZoneDisplayCities = (timeZone) => {
  const zone = getTimeZoneInfo(timeZone)
  return uniqueValues(zone?.mainCities).slice(0, DISPLAY_CITY_LIMIT)
}

const getAdditionalSearchCities = (zone) => ADDITIONAL_SEARCH_CITIES_BY_ZONE[zone?.name] || []

const getTimeZoneSearchCities = (zone) =>
  uniqueValues([...(zone?.mainCities || []), ...getAdditionalSearchCities(zone)])

// The grey line under each timezone in the picker: "GMT-04 - New York, Brooklyn".
//
// atTime is the moment the offset is read at — pass the call's start time so the
// picker shows the offset that will actually apply on the day of the call, which
// differs from today's whenever daylight saving starts or ends in between.
export const getTimeZoneSecondaryLine = (timeZone, atTime = Date.now()) => {
  if (!timeZone) return ''
  const zone = getTimeZoneInfo(timeZone)
  const offsetInMinutes = getOffsetInMinutesAt(zone || timeZone, atTime) ?? zone?.rawOffsetInMinutes
  const offset = formatOffset(offsetInMinutes)
  const cities = getTimeZoneDisplayCities(zone).join(', ')
  if (offset && cities) return `GMT${offset} - ${cities}`
  if (offset) return `GMT${offset}`
  return getCanonicalTimeZoneName(timeZone)
}

const getNameSearchVariants = (name = '') => [name, name.replace(/[/_]/g, ' ')]

const getGroupSearchVariants = (group = []) =>
  group.reduce((variants, name) => variants.concat(getNameSearchVariants(name)), [])

// offsetInMinutes is the offset the zone is on for the call being scheduled, which
// is included alongside the standard one so both spellings are searchable: Sydney
// is GMT+10 as standard but GMT+11 in December, and a coach booking a December call
// sees +11 in the list and will type that.
const getSearchValues = (zone, offsetInMinutes, atTime) => [
  getTimeZoneTitle(zone),
  ...getNameSearchVariants(zone.name),
  ...getGroupSearchVariants(zone.group),
  ...getTimeZoneSearchCities(zone),
  zone.countryName,
  zone.countryCode,
  zone.abbreviation,
  zone.alternativeName,
  ...getOffsetSearchValues(zone.rawOffsetInMinutes),
  ...getOffsetSearchValues(offsetInMinutes),
  getTimeZoneSecondaryLine(zone, atTime),
  zone.rawFormat,
  zone.rawFormat ? `GMT${zone.rawFormat}` : '',
]

const timeZoneSearchTextCache = new WeakMap()

// Cached per zone and per offset rather than per zone alone, because the text now
// depends on the call date. A zone only ever has a couple of distinct offsets, so
// this stays a handful of entries however many dates the coach tries.
const getTimeZoneSearchText = (timeZone, atTime) => {
  const zone = getTimeZoneInfo(timeZone)
  if (!zone) return normalizeTimeZoneSearchText(getCanonicalTimeZoneName(timeZone))

  const offsetInMinutes = getOffsetInMinutesAt(zone, atTime)
  let textByOffset = timeZoneSearchTextCache.get(zone)
  if (!textByOffset) {
    textByOffset = new Map()
    timeZoneSearchTextCache.set(zone, textByOffset)
  }
  if (textByOffset.has(offsetInMinutes)) return textByOffset.get(offsetInMinutes)

  const searchText = [...new Set(getSearchValues(zone, offsetInMinutes, atTime))]
    .map(normalizeTimeZoneSearchText)
    .filter(Boolean)
    .join(' ')
  textByOffset.set(offsetInMinutes, searchText)
  return searchText
}

// atTime is the call's start time, so an offset search matches what the list is
// showing for that date. Without it the search falls back to today.
export const timeZoneMatchesSearch = (timeZone, rawQuery, atTime = Date.now()) => {
  // Exact GMT/UTC offset searches should match only zones at that offset, not any text hit.
  // Either the zone's standard offset or the one it is on for this call counts as a
  // match, because half the year those are different numbers and the coach has no
  // way to know which one this app stores.
  const signedOffsetQuery = parseSignedOffsetQuery(rawQuery)
  if (signedOffsetQuery != null) {
    const zone = getTimeZoneInfo(timeZone)
    return zone?.rawOffsetInMinutes === signedOffsetQuery || getOffsetInMinutesAt(zone, atTime) === signedOffsetQuery
  }

  const query = normalizeTimeZoneSearchText(rawQuery)
  if (!query) return true
  return getTimeZoneSearchText(timeZone, atTime).includes(query)
}
